import { Injectable } from '@angular/core';
import { handleApiCallAsync } from '../../../../utils/general-functions';
import { IUpdateTakeImage } from '../../../http-models/project/take/take-http-request-model';
import { IProject } from '../../../../models/project/project-model';
import { ITake, ITakeInDTO, ITakeUpdate, TakeUpdateableProperties } from '../../../../models/project/take/take-model';
import { IScene } from '../../../../models/project/scene-model';
import {
    IUpdateTakeImageBody,
    TakeDynamicLottieApiService,
} from '../../../api/auth/projects/take/take-dynamic-lottie-api.service';
import { ProjectStoreService } from '../../../state-management/project/project-store.service';
import { TakeConverterService } from '../../convertors/take/take-converter.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UploaderService } from '../../../../components/file-drop/uploader.service';
import { TakeNotFoundError } from '../../../../models/errors/project-errors/takes-errors';
import { TakeApiService } from '../../../api/auth/projects/take/take-api.service';
import { MissingArgumentsError } from '../../../../models/errors/general.errors';
import { UPLOADER_SERVICE_UNIQUE_IDS_ENUM } from '../../../../constants/shared/uploader-service-unique-ids';
import { AvailableFontManagerAbstractClass } from '../../../../components/major/style-settings/font/available-font-manager-abstract-class';
import { AvailableFontsStoreService } from '../../../state-management/configs/available-fonts-store.service';
import { IExtraFontSettings, IFontIDWithFamily } from 'src/app/models/style-font-model';
import { IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import { Subject } from 'rxjs';
import { IOptionalStyleForFont } from '../../../../models/configs/fonts.model';

@Injectable()
export class TakeManagerService extends AvailableFontManagerAbstractClass {
    currentWorkingTake: ITake;
    currentWorkingDynamicText: IDynamicLottieChange;

    private _triggerTakeToPreviewSubject = new Subject<void>();
    public triggerTakeToPreview$ = this._triggerTakeToPreviewSubject.asObservable();

    constructor(
        private projectStoreService: ProjectStoreService,
        private takeDynamicLottieApi: TakeDynamicLottieApiService,
        private takeConvertor: TakeConverterService,
        private takeApiService: TakeApiService,
        private uploaderService: UploaderService,
        private availableFontService: AvailableFontsStoreService
    ) {
        super();
        this.availableFontService.availableFonts$.subscribe((fonts) => {
            this._allFontsSubject.next(fonts);
        });
    }

    public setCurrentWorkingState(take: ITake, dynamicText: IDynamicLottieChange) {
        this.currentWorkingTake = take;
        this.currentWorkingDynamicText = dynamicText;
    }

    public newFontSelectedAsync(
        data: IFontIDWithFamily,
        currentFont: IExtraFontSettings,
        optionalStyle: IOptionalStyleForFont
    ): Promise<void> {
        if (!this.currentWorkingDynamicText?.font || !this.currentWorkingTake) {
            return;
        }
        const dynamicTextFromTake = this.currentWorkingTake.copy.dynamicLottieChanges.find((dt) => {
            return dt.id === this.currentWorkingDynamicText.id;
        });
        if (!dynamicTextFromTake) {
            return Promise.resolve();
        }
        dynamicTextFromTake.font.id = optionalStyle.specialFontId;
        dynamicTextFromTake.font.family = data.family;
        dynamicTextFromTake.font.style = currentFont.style;

        console.log(`TAKE 1`, this.currentWorkingTake);
        this._triggerTakeToPreviewSubject.next();
        //// Wer are not updating the server because when saving scene setup the server is updated
        return Promise.resolve();
    }

    async getNewImageFromTakeAsync(
        project: IProject,
        scene: IScene,
        take: ITake,
        updateActions: IUpdateTakeImage,
        dynamicLottieId: string
    ) {
        if (!updateActions) {
            throw new Error(`Could not get new image for take because no action was provided`);
        }
        const projectId = project.id;
        const sceneId = scene.id;
        const takeId = take.id;
        const design = project.designGroup.chosenDesign;

        const body: IUpdateTakeImageBody = {
            existingImage: updateActions.pickAsset
                ? { relativeImagePath: updateActions.pickAsset.imageRelativePath }
                : null,
            selectPhoto: updateActions.pickImageFromUnsplash
                ? { photoId: updateActions.pickImageFromUnsplash.photoId }
                : null,
        };
        const { data: inTake, error } = await handleApiCallAsync(
            this.takeDynamicLottieApi.updateDynamicLottieImagePath$(projectId, sceneId, takeId, dynamicLottieId, body)
        );
        if (error) {
            throw error;
        }
        if (!inTake) {
            throw new Error(`No take in body response`);
        }
        this.replaceOrAddTakeAsync(project, scene, inTake, false, false);
        return inTake;
    }

    async uploadNewImageForTakeAsync(
        formData: FormData,
        project: IProject,
        scene: IScene,
        take: ITake,
        dynamicLottieId: string,
        type: UPLOADER_SERVICE_UNIQUE_IDS_ENUM
    ) {
        const projectId = project.id;
        const sceneId = scene.id;
        const takeId = take.id;
        const design = project.designGroup.chosenDesign;

        const { data: inTake, error } = await this.uploaderService.uploadFileAsync<ITakeInDTO>(
            type,
            this.takeDynamicLottieApi.uploadImage$(formData, projectId, sceneId, takeId, dynamicLottieId)
        );

        if (error) {
            throw error;
        }
        if (!inTake) {
            throw new Error(`No take in body response`);
        }
        this.replaceOrAddTakeAsync(project, scene, inTake, false, false);
        return inTake;
    }

    public async replaceOrAddTakeAsync(
        project: IProject,
        scene: IScene,
        inTake: ITakeInDTO,
        waitForLocalVideos: boolean,
        setLocalVideoLayer: boolean,
        trims: { trimStart: number; trimEnd: number } | null = null,
        toAddOrReplaceTakeImmediate: boolean = true
    ) {
        const layout = scene.composition.layouts[0];
        const localTake = await this.takeConvertor.inToLocalAsync(
            inTake,
            null,
            layout,
            waitForLocalVideos,
            project.id,
            scene.id,
            scene.name,
            scene.isGraphicScene,
            project.indexDBData,
            setLocalVideoLayer,
            trims
        );
        if (toAddOrReplaceTakeImmediate) {
            this.addOrReplaceTake(project, scene, localTake);
        }
        return localTake;
    }

    public addOrReplaceTake(project: IProject, scene: IScene, localTake: ITake) {
        return this.projectStoreService.replaceOrAddProjectTakes(project.id, scene.id, localTake);
    }

    public updateTakePropertyAsync<K extends keyof TakeUpdateableProperties>(
        project: IProject,
        scene: IScene,
        takeId: string,
        propertiesToUpdate: ITakeUpdate<K>[],
        waitForLocalVideos: boolean,
        setLocalVideoLayer: boolean
    ) {
        if (!project || !scene || !takeId || !propertiesToUpdate) {
            throw new MissingArgumentsError(
                `Could not update demo take because one of the arguments is null or undefined.`
            );
        }
        if (propertiesToUpdate.length === 0) {
            return;
        }

        const takeToUpdate = this.getTake(scene, takeId);
        if (!takeToUpdate) {
            throw new Error(`Take with ID ${takeId} not found in scene ${scene}.`);
        }
        return new Promise<boolean>((resolve, reject) => {
            this.takeApiService.updateTake$(project.id, scene.id, takeId, propertiesToUpdate).subscribe({
                next: async (inTake) => {
                    if (!inTake) {
                        console.error(`Something strange happened while trying to update take.`);
                        return resolve(false);
                    }
                    await this.replaceOrAddTakeAsync(project, scene, inTake, waitForLocalVideos, setLocalVideoLayer);
                    return resolve(true);
                },
                error(err: HttpErrorResponse) {
                    if (err.status === 500) {
                        //TODO: try again
                    }
                    console.error(`There was an error while updating take: ${err}`);
                    return reject(`There was an error while updating take`);
                },
            });
        });
    }

    public getTake(scene: IScene, takeId: string) {
        const takes = scene?.takes;

        const updateTake = takes?.find((take) => take.id === takeId);
        if (!updateTake) {
            throw new TakeNotFoundError(`Could not update take because take with takeid: ${takeId} is not found`);
        }
        return updateTake;
    }
}
